import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type ImagingInstance,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/imaging-instance";
import { useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { getFeasibleExamTypes } from "../dicom-preset-mapping";

export type ImagingInstancePresetSummary = {
  instances: ReadonlyArray<ImagingInstance>;
  /** The dominant preset (most common non-null), null if none have a preset */
  dominantPreset: string | null;
  /**
   * Feasible exam types based on the dominant preset.
   * - null  → show full picker (unknown preset or no preset)
   * - [one] → can auto-select (still confirm for CHEST)
   * - [two+] → show restricted picker
   */
  feasibleExamTypes: ExamType[] | null;
  isFetching: boolean;
};

export const encounterImagingInstancesQuery = (
  accessionNumber: string | null | undefined,
  dicomStudyId: string | null | undefined,
) => {
  const byStudy =
    dicomStudyId != null ? [{ dicomStudyId: { $eq: dicomStudyId } }] : [];
  const byAccession =
    accessionNumber != null
      ? [{ imagingStudyAccessionNumber: { $eq: accessionNumber } }]
      : [];
  const clauses = [...byAccession, ...byStudy];
  return (col: RxCollection<ImagingInstance>) =>
    clauses.length > 0
      ? col.find({ selector: { $or: clauses }, sort: [{ id: "asc" }] })
      : col.find({ selector: { id: { $exists: false } } });
};

export function useEncounterImagingInstances(
  accessionNumber: string | null | undefined,
  dicomStudyId?: string | null,
): ImagingInstancePresetSummary {
  const { result: instances, isFetching } = useRxData<ImagingInstance>(
    RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
    encounterImagingInstancesQuery(accessionNumber, dicomStudyId),
  );

  const dominantPreset = useMemo((): string | null => {
    if (!instances || instances.length === 0) return null;
    const counts = new Map<string, number>();
    for (const inst of instances) {
      const preset = inst.dicomPreset;
      if (preset) counts.set(preset, (counts.get(preset) ?? 0) + 1);
    }
    if (counts.size === 0) return null;
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }, [instances]);

  const feasibleExamTypes = useMemo(
    () => (dominantPreset ? getFeasibleExamTypes(dominantPreset) : null),
    [dominantPreset],
  );

  return {
    instances: instances ?? [],
    dominantPreset,
    feasibleExamTypes,
    isFetching,
  };
}

/**
 * Nothing stores which exam owns an instance — the only record is its id
 * appearing in some exam's `images`. Writers must subtract this set or they
 * hand the same image to every exam in the encounter.
 */
export function collectAssignedExamImageIds(
  exams: ReadonlyArray<Pick<EpocusExam, "images">>,
): ReadonlySet<string> {
  return new Set(exams.flatMap((exam) => exam.images.map((img) => img.id)));
}

/**
 * Wait for `isFetching` before writing: an unsettled query yields an empty set,
 * which reads as "nothing is claimed".
 */
export function useAssignedExamImageIds(
  encounterId: string | null | undefined,
): { assignedImageIds: ReadonlySet<string>; isFetching: boolean } {
  const { result: exams, isFetching } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      encounterId ? collection?.find({ selector: { encounterId } }) : undefined,
  );

  const assignedImageIds = useMemo(
    () => collectAssignedExamImageIds(exams ?? []),
    [exams],
  );

  return { assignedImageIds, isFetching };
}

export function imagingInstanceToExamImage(instance: ImagingInstance) {
  return {
    id: instance.id,
    previewS3Key: instance.previewS3Key,
    viewLabel: null as string | null,
    dicomPreset: instance.dicomPreset ?? null,
    physicalDeltaMm: instance.physicalDeltaMm ?? null,
  };
}
